import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="label-overline mb-4">Sign in</div>
      <h1 className="font-display text-4xl font-black tracking-tight mb-2">Welcome back.</h1>
      <p className="text-ink-muted mb-8">Resume your audits and download reports.</p>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand focus:border-brand"
            placeholder="you@company.com"
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full bg-white border border-ink rounded-sm px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              placeholder="••••••••"
            />
            <button type="button" onClick={() => setShow((s) => !s)}
              data-testid="login-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink">
              {show ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </Field>
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit-button"
          className="w-full bg-ink text-white font-semibold py-3 rounded-sm hover:bg-brand transition-colors hard-shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="mt-8 text-sm text-ink-muted">
        New here?{" "}
        <Link to="/register" data-testid="login-register-link" className="text-brand font-semibold hover:underline">
          Create an account
        </Link>
      </div>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="label-overline mb-4">Create account</div>
      <h1 className="font-display text-4xl font-black tracking-tight mb-2">Start auditing.</h1>
      <p className="text-ink-muted mb-8">Free to start. No card required.</p>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <Field label="Name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name-input"
            className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand"
            placeholder="Jane Doe"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email-input"
            className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand"
            placeholder="you@company.com"
          />
        </Field>
        <Field label="Password" hint="Min 6 characters">
          <input
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password-input"
            className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand"
            placeholder="••••••••"
          />
        </Field>
        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit-button"
          className="w-full bg-ink text-white font-semibold py-3 rounded-sm hover:bg-brand transition-colors hard-shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
        >
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="mt-8 text-sm text-ink-muted">
        Already have one?{" "}
        <Link to="/login" data-testid="register-login-link" className="text-brand font-semibold hover:underline">
          Sign in
        </Link>
      </div>
    </AuthLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="label-overline">{label}</span>
        {hint && <span className="text-xs text-ink-muted">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <Link to="/" className="relative inline-flex items-center" data-testid="auth-logo">
          <Logo variant="dark" size="h-10" />
        </Link>
        <div className="relative">
          <div className="label-overline text-white/60 mb-4">Why Auditor</div>
          <h2 className="font-display text-4xl font-black tracking-tight leading-tight mb-4">
            One URL.<br />Sixty checks.<br />One PDF.
          </h2>
          <p className="text-white/70 max-w-md">
            We bundle SEO, performance, security, accessibility, and tech-stack
            detection into a single report — with a prioritised remediation plan
            you can act on the same day.
          </p>
        </div>
        <div className="relative mono text-xs text-white/40">© 2026 Auditmysite.ca · Division of Yellow Thinking</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}
